import { X, Plus, Minus, ShoppingBag, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';

const CartDrawer = () => {
  const { 
    cart, 
    isCartOpen, 
    setIsCartOpen, 
    updateQuantity, 
    removeFromCart, 
    getCartTotal,
    isLoading 
  } = useCart();

  const total = getCartTotal();

  return (
    <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
      <SheetContent className="bg-charcoal border-white/5 w-full sm:w-[450px] flex flex-col" data-testid="cart-drawer">
        <SheetHeader className="border-b border-white/5 pb-4">
          <SheetTitle className="font-serif text-2xl text-white flex items-center gap-3">
            <ShoppingBag className="w-6 h-6 text-gold-500" />
            Your Cart
          </SheetTitle>
        </SheetHeader>

        {cart.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center">
            <ShoppingBag className="w-16 h-16 text-white/10" />
            <p className="text-white/50">Your cart is empty</p>
            <Button
              onClick={() => setIsCartOpen(false)}
              className="bg-transparent border border-white/20 text-white hover:border-gold-500 hover:text-gold-500 rounded-none px-6 py-3 text-sm uppercase tracking-widest"
            >
              Continue Shopping
            </Button>
          </div>
        ) : (
          <>
            <ScrollArea className="flex-1 -mx-6 px-6">
              <div className="space-y-6 py-6">
                {cart.map((item) => (
                  <div key={item.id} className="flex gap-4" data-testid={`cart-item-${item.id}`}>
                    {/* Product Image */}
                    <div className="w-24 h-24 bg-obsidian flex-shrink-0">
                      <img
                        src={item.product?.image}
                        alt={item.product?.name}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Product Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-serif text-white truncate">{item.product?.name}</h4>
                      <p className="text-xs text-gold-500 uppercase tracking-wider mt-1">
                        Size: {item.size}
                      </p>
                      <p className="font-serif text-gold-400 italic mt-2">
                        ${item.product?.price?.toLocaleString()}
                      </p>

                      {/* Quantity Controls */}
                      <div className="flex items-center gap-3 mt-3">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-7 h-7 text-white/60 hover:text-gold-500 hover:bg-white/5"
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          disabled={isLoading}
                          data-testid={`decrease-qty-${item.id}`}
                        >
                          <Minus className="w-3 h-3" />
                        </Button>
                        <span className="text-white w-6 text-center">{item.quantity}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-7 h-7 text-white/60 hover:text-gold-500 hover:bg-white/5"
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          disabled={isLoading}
                          data-testid={`increase-qty-${item.id}`}
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-7 h-7 text-white/40 hover:text-red-500 hover:bg-white/5 ml-auto"
                          onClick={() => removeFromCart(item.id)}
                          disabled={isLoading}
                          data-testid={`remove-item-${item.id}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            {/* Footer */}
            <div className="border-t border-white/5 pt-6 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-white/60 uppercase tracking-wider text-sm">Subtotal</span>
                <span className="font-serif text-2xl text-gold-500" data-testid="cart-total">
                  ${total.toLocaleString()}
                </span>
              </div>
              <p className="text-xs text-white/40">
                Shipping and taxes calculated at checkout
              </p>
              <Separator className="bg-white/5" />
              <Link to="/checkout" onClick={() => setIsCartOpen(false)}>
                <Button
                  className="w-full bg-gold-500 text-black hover:bg-gold-400 rounded-none py-6 text-sm uppercase tracking-widest font-bold transition-all hover:scale-[1.01] btn-glow"
                  data-testid="checkout-btn"
                >
                  Proceed to Checkout
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
